import * as React from "react";
import {GridColDef} from "@mui/x-data-grid";
import {formatTime} from "@/helper/method";
import {BtnType, ProTable} from "@/widget/ProTable/ProTable";
import {TrademarkPageVO, useQueryTrademarkPage} from "@/service/trademark.s";
import {WriteTrademark} from "@/pages/Trademark/WriteTrademark";
import {useParams} from "react-router-dom";
import {useEffect, useMemo} from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {Avatar, ButtonGroup} from "@mui/material";
import Button from "@mui/material/Button";
import {NavigateBefore, NavigateNext} from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import SvgIcon from "@mui/material/SvgIcon";
import {defaultPageParams, permScope, roleAlias} from "@/helper/constant";
import {useClaimProduct} from "@/service/contract.s";
import {useGlobalActions} from "@/store/global.c";
import {ViewTrademark} from "@/pages/Trademark/ViewTrademark";
import {WriteStatus} from "@/widget/WriteStatus/ReviewStatus";
import {useAuthInfo} from "@/store/account.c";
import {SellerAvatar} from "@/widget/SellerAvatar/SellerAvatar";

const trademarkCode: { [key: string]: string } = {
    'all': "all_trademark",
    "unclaim": "unclaim_trademark",
    "duty": "duty_trademark",
}

const trademarkStatus: { [key: string]: number } = {
    'all': 0,
    "unclaim": 5,
    "duty": 6,
}

const initPage = (page: string): [number, string] => {
    return [trademarkStatus[page], trademarkCode[page]]
}

export const TrademarkListPage = () => {
    const {page} = useParams()
    const {mutateAsync: claimProductMutate} = useClaimProduct()
    const {setToast} = useGlobalActions()
    const [writeStatus, code] = initPage(page!)
    const {
        mutate: trademarkPageMutate,
        data: trademarkPageData,
        isPending: trademarkPagePending,
        isIdle
    } = useQueryTrademarkPage(writeStatus)
    const {role: {dataScope, roleAlias: alias}} = useAuthInfo()

    const allColumns: GridColDef<TrademarkPageVO>[] = [
        {
            field: "pattern",
            headerName: "图样",
            headerAlign: "left",
            align: "left",
            display: 'flex',
            width: 64,
            renderCell: ({row}) => {
                return <Avatar sx={{width: 32, height: 32, borderRadius: 0.4, border: '1px solid #f2f2f2', bgcolor: '#60B5FF', "& .MuiAvatar-img": {objectFit: 'fill'}}}
                               variant="square" src={row.pattern}>
                    <SvgIcon>
                        <svg fill="#c2e8ff" width="64px" height="64px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" className="icon">
                            <g id="SVGRepo_bgCarrier" strokeWidth="0"/>
                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"/>
                            <g id="SVGRepo_iconCarrier">
                                <path
                                    d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372zm87.5-334.7c34.8-12.8 78.4-49 78.4-119.2 0-71.2-45.5-131.1-144.2-131.1H378c-4.4 0-8 3.6-8 8v410c0 4.4 3.6 8 8 8h54.5c4.4 0 8-3.6 8-8V561.2h88.7l74.6 159.2c1.3 2.8 4.1 4.6 7.2 4.6h62a7.9 7.9 0 0 0 7.1-11.5l-80.6-164.2zM522 505h-81.5V357h83.4c48 0 80.9 25.3 80.9 75.5 0 46.9-29.8 72.5-82.8 72.5z"/>
                            </g>
                        </svg>
                    </SvgIcon>
                </Avatar>
            }
        },
        {
            field: "trademarkName",
            headerName: "商标名称",
            headerAlign: "left",
            align: "left",
            flex: 0.5,
            valueFormatter: (value: string) => {
                return value || "待定"
            }
        },
        {
            field: "applyNo",
            headerName: "登记号",
            headerAlign: "left",
            align: "left",
            flex: 0.35,
            valueFormatter: (value: string) => value || "-"
        },
        {
            field: "trademarkStatus",
            headerName: "商标状态",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            valueFormatter: (value: string) => value || "-"
        },
        {
            field: "writer",
            headerName: "撰稿人",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
        },
        {
            field: "writeStatus",
            headerName: "撰稿状态",
            flex: 0.4,
            headerAlign: "center",
            align: "center",
            display: "flex",
            renderCell: ({row}) => {
                return <WriteStatus writeStatus={row?.writeStatus} writeStatusId={row?.writeStatusId}/>
            }
        },
        {field: "customerName", headerName: "客户名称", flex: 0.5},
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "insideDeadline",
            headerName: "内部期限",
            align: "center",
            headerAlign: "center",
            flex: 0.4,
            valueFormatter: (value) => formatTime("D", value),
        },
        // {
        //     field: "receiveTime",
        //     headerName: "领取时间",
        //     headerAlign: "center",
        //     align: "center",
        //     valueFormatter: (value) => formatTime("D", value),
        // },
    ];

    const dutyColumns: GridColDef<TrademarkPageVO>[] = [
        {
            field: "trademarkName",
            headerName: "商标名称",
            headerAlign: "left",
            align: "left", flex: 0.6,
            valueFormatter: value => value || "待定",
        },
        {
            field: "category",
            headerName: "申请类型",
            headerAlign: "left",
            align: "left", flex: 0.6
        },
        {
            field: "customerName",
            headerName: "客户名称",
            flex: 0.5
        },
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "trademarkStatus",
            headerName: "申报状态",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "receiveAt",
            headerName: "领取时间",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    const unclaimColumns: GridColDef<TrademarkPageVO>[] = [
        {
            field: "trademarkName",
            headerName: "商标名称",
            headerAlign: "left",
            align: "left", flex: 0.6,
            valueFormatter: value => value || "待定",
        },
        {
            field: "classify",
            headerName: "类型",
            headerAlign: "left",
            align: "left", flex: 0.4,
            valueFormatter: () => "商标注册",
        },
        {field: "customerName", headerName: "客户名称", flex: 0.6},
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4
        },
        {
            field: "num",
            headerName: "数量",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "price",
            headerName: "金额",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "dealTime",
            headerName: "成交时间",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
        {
            field: "updatedAt",
            headerName: "更新时间",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    useEffect(() => {
        if (isIdle == false) {
            trademarkPageMutate({current: 1, size: 20})
        }
    }, [writeStatus]);


    const columns = useMemo(() => {
        switch (writeStatus) {
            case 0:
                return allColumns
            case 5:
                return unclaimColumns
            case 6:
                return dutyColumns
        }
        return []
    }, [writeStatus]);


    const btnCallback = async ({btn, keys, onSearch}: BtnType<TrademarkPageVO>) => {
        if (btn.alias == "claim") {
            if (keys.length <= 10 && keys.length > 0) {
                const result = await claimProductMutate({ids: keys, productTypeId: 3})
                if (result.code == 0) {
                    onSearch && onSearch(defaultPageParams)
                    setToast({message: result.msg, open: true, severity: 'success'})
                }
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
    }

    return (
        <ProTable<TrademarkPageVO>
            loading={trademarkPagePending}
            columnVisibilityModel={{
                seller: !(alias == roleAlias["marketing"] && dataScope == permScope['self']),
                writer: !(alias == roleAlias["processer"] && dataScope == permScope['self'])
            }}
            renderDrawer={{view: <ViewTrademark/>, write: <WriteTrademark/>}}
            columns={columns}
            btnCallback={btnCallback}
            drawerProps={{
                view: {
                    bodyWidth: 640, headerHeight: 68, hiddenFooter: true, viewBtnCallback: (row: TrademarkPageVO) => {
                        return {
                            custom: true,
                            title: row.trademarkName || "待定",
                            businessId: row.contractId,
                            switchButton: <ButtonGroup size={'small'} sx={{
                                "& .MuiButton-startIcon": {marginRight: 0},
                                "& .MuiButton-endIcon": {marginLeft: 0}
                                , "& .MuiButtonGroup-grouped": {minWidth: 20, height: 20, width: 12, bgcolor: '#f2f4fb',}
                            }} variant="outlined">
                                <Button size={'small'} startIcon={<NavigateBefore/>}/>
                                <Button size={'small'} endIcon={<NavigateNext/>}/>
                            </ButtonGroup>,
                            icon: <SvgIcon>
                                <svg fill="#60B5FF" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" className="icon">
                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"/>
                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"/>
                                    <g id="SVGRepo_iconCarrier">
                                        <path
                                            d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372zm87.5-334.7c34.8-12.8 78.4-49 78.4-119.2 0-71.2-45.5-131.1-144.2-131.1H378c-4.4 0-8 3.6-8 8v410c0 4.4 3.6 8 8 8h54.5c4.4 0 8-3.6 8-8V561.2h88.7l74.6 159.2c1.3 2.8 4.1 4.6 7.2 4.6h62a7.9 7.9 0 0 0 7.1-11.5l-80.6-164.2zM522 505h-81.5V357h83.4c48 0 80.9 25.3 80.9 75.5 0 46.9-29.8 72.5-82.8 72.5z"/>
                                    </g>
                                </svg>
                            </SvgIcon>,
                            tags: <Stack direction={'row'} spacing={1.4} alignContent={'center'}>
                                <Chip size={'small'} variant={'outlined'} sx={{height: 16}} color={'warning'} label={<Typography fontSize={10}>{row.trademarkStatus}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'error'}
                                      label={<Typography fontSize={10}>{row.nodeNames || "已通过"}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'success'}
                                      label={<Typography fontSize={10}>{row.category || "-"}</Typography>}/>
                            </Stack>, instanceId: row.instanceId
                        }
                    }
                }
            }}
            data={trademarkPageData?.data}
            onSearch={(params) => {
                trademarkPageMutate(params);
            }}
            code={code}
        />
    );
};
